import { useEffect, useRef } from "react"
import { Card } from "@/components/ui/Card"
import ProductLink from "@/components/productQuickView/ProductLink"
import GraphsSection from "@/components/productQuickView/sections/GraphsSection"
import { useQuickView } from "@/components/productQuickView/useQuickView"
import { Button } from "@/components/ui/Button"
import type { Person } from "@/lib/person"
import { useMarket } from "@/lib/useMarket"
import type { Channel, Detail, MentionNames, ReviewAction } from "../types"
import type { ActionDraft } from "./ActionEditor"
import ActionsSection from "./ActionsSection"
import MentionedBy from "./MentionedBy"
import Section from "./Section"
import StockNotes from "./StockNotes"
import WhereItSits from "./WhereItSits"

interface Props {
  detail: Detail
  actions: ReviewAction[]
  mentionedBy: ReviewAction[]
  names: MentionNames
  actionTypes: string[]
  channels: Channel[]
  people: Person[]
  onCreate: (draft: ActionDraft, done: (saved: boolean) => void) => void
  onUpdate: (id: string, draft: ActionDraft, done: (saved: boolean) => void) => void
  onDelete: (id: string) => void
}

// The product the room is talking about right now, large: where the stock
// sits, how it has been moving, and the actions decided for it. The graphs
// are the product quick view's, loaded the same way, and load again when an
// action is saved, changed or deleted so its line shows right away.
export default function ProductPanel({ detail, actions, mentionedBy, names, actionTypes, channels, people, onCreate, onUpdate, onDelete }: Props) {
  const market = useMarket()
  const quickView = useQuickView(detail.external_id, market.slug)
  const { reload } = quickView

  const marked = actions.map((a) => `${a.id}:${a.action_type}:${a.starts_on ?? a.created_on}`).join(",")
  const lastMarked = useRef({ product: detail.external_id, marked })
  useEffect(() => {
    const last = lastMarked.current
    lastMarked.current = { product: detail.external_id, marked }
    if (last.product === detail.external_id && last.marked !== marked) reload()
  }, [detail.external_id, marked, reload])

  return (
    <Card>
      <div className="px-6 py-5">
        <h2>
          <ProductLink externalId={detail.external_id} className="text-xl font-semibold">{detail.name}</ProductLink>
        </h2>
        <div className="mt-0.5 text-sm text-muted-foreground">
          {[detail.short_name, `#${detail.external_id}`, detail.group_name ?? "Other"].filter(Boolean).join(" · ")}
        </div>
        <div className="mt-4">
          <StockNotes detail={detail} />
        </div>
      </div>

      <Section title="Where it sits">
        <WhereItSits detail={detail} />
      </Section>

      {quickView.data ? (
        <GraphsSection data={quickView.data} />
      ) : (
        <Section title="Graphs">
          {quickView.failed ? (
            <div className="flex flex-col items-start gap-3">
              <p className="text-sm text-foreground">Could not load the graphs.</p>
              <Button variant="outline" size="sm" onClick={quickView.reload}>Try again</Button>
            </div>
          ) : (
            <p className="text-sm text-foreground">Loading…</p>
          )}
        </Section>
      )}
      {mentionedBy.length > 0 && (
        <div className="px-6 pb-5">
          <MentionedBy mentions={mentionedBy} names={names} />
        </div>
      )}

      <ActionsSection
        actions={actions}
        actionTypes={actionTypes}
        channels={channels}
        people={people}
        names={names}
        onCreate={onCreate}
        onUpdate={onUpdate}
        onDelete={onDelete}
      />
    </Card>
  )
}
